import {AfterViewInit, Component, ElementRef, inject, OnInit, OnDestroy, signal, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {GenericFormComponent} from '../../../../../shared/components/generic-form/generic-form.component';
import {AuthService} from '../../../../../core/auth/auth.service';
import {LoginData} from '../../../../../shared/model/login-data';
import {ApiResponse} from '../../../../../shared/model/api-response';

declare var google: any;

@Component({
  selector: 'app-login-form',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink, GenericFormComponent],
  templateUrl: './login-form.component.html',
  styleUrl: './login-form.component.css'
})
export class LoginFormComponent implements OnInit, AfterViewInit, OnDestroy {
  private authService: AuthService = inject(AuthService);
  private router: Router = inject(Router);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private fb: FormBuilder = inject(FormBuilder);

  @ViewChild('googleBtn') googleBtn!: ElementRef;

  loginForm: FormGroup = this.fb.group({
    userName: ['', [Validators.required]],
    password: ['', [Validators.required]],
    rememberMe: [false]
  });

  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  infoMessage = signal<string | null>(null);

  private retryTimeoutId: any = null;
  static gsiInitialized = false;
  static currentInstance: LoginFormComponent | null = null;

  ngOnInit() {
    if (this.route.snapshot?.queryParams?.['emailChanged'] === 'true') {
      this.infoMessage.set('Indirizzo email aggiornato con successo! Ti abbiamo inviato un link di verifica alla tua nuova email. Clicca sul link prima di effettuare nuovamente il login.');
    }
  }

  ngAfterViewInit() {
    LoginFormComponent.currentInstance = this;
    this.initGoogleBtnWithRetry();
  }

  ngOnDestroy() {
    if (LoginFormComponent.currentInstance === this) {
      LoginFormComponent.currentInstance = null;
    }
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  private initGoogleBtnWithRetry(retries = 10) {
    if (typeof google !== 'undefined' && google.accounts) {
      if (!LoginFormComponent.gsiInitialized) {
        google.accounts.id.initialize({
          client_id: '473112692113-48g8tnii5petva5agsbohnorb1j0sfuh.apps.googleusercontent.com',
          callback: (response: any) => {
            if (LoginFormComponent.currentInstance) {
              LoginFormComponent.currentInstance.handleGoogleCredentialResponse(response);
            }
          }
        });
        LoginFormComponent.gsiInitialized = true;
      }

      if (this.googleBtn && this.googleBtn.nativeElement) {
        google.accounts.id.renderButton(this.googleBtn.nativeElement, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: '320',
          locale: 'it'
        });
      }
    } else if (retries > 0) {
      this.retryTimeoutId = setTimeout(() => this.initGoogleBtnWithRetry(retries - 1), 300);
    }
  }

  handleGoogleCredentialResponse(response: any) {
    if (!response || !response.credential) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.authService.makeGoogleLogin(response.credential).subscribe({
      next: (apiResponse: ApiResponse<string>) => {
        if (apiResponse.success && apiResponse.data) {
          this.authService.completeLogin(apiResponse.data, true).subscribe({
            next: () => {
              this.isLoading.set(false);
              this.router.navigate(['/events']);
            },
            error: () => this.isLoading.set(false),
          });
          return;
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.errorMessage.set('Impossibile effettuare l\'accesso con Google.');
      }
    });
  }

  onSubmit() {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const loginData: LoginData = {
      userName: this.loginForm.value.userName,
      password: this.loginForm.value.password
    };
    const rememberMe: boolean = this.loginForm.value.rememberMe;

    this.authService.makeLogin(loginData).subscribe({
      next: (apiResponse: ApiResponse<string>) => {
        if (apiResponse.success && apiResponse.data) {
          this.authService.completeLogin(apiResponse.data, rememberMe).subscribe({
            next: () => {
              this.isLoading.set(false);
              this.router.navigate(['/events']);
            },
            error: () => this.isLoading.set(false),
          });
          return;
        }
        this.isLoading.set(false);
      }, error: () => {
        this.isLoading.set(false);
      }
    });
  }
}






